import React, { useState, useEffect, useRef } from 'react';
import cl from './Style.module.css';
import { Link } from 'react-router-dom';
import { useLoader } from '../../hooks/useLoader';
import AdminService from '../../API/AdminService';
import { useNavigate } from 'react-router-dom';
import Spinner from '../../components/UI/Spinner/Spinner';
import PageIntro from '../../components/PageIntro/PageIntro';
import logger from '../../utils/logger';
import {
    IconSearch, IconPlus, IconChevronUp, IconChevronDown, IconStudents,
} from '../../components/Icons';

// Иконка-индикатор направления сортировки колонки таблицы.
// Неактивная колонка: пара бледных стрелок.
// Активная: одна стрелка в брендовом цвете.
function SortIcon({ active, dir }) {
    if (!active) {
        return (
            <span style={{
                display: 'inline-flex',
                flexDirection: 'column',
                marginLeft: 4,
                color: 'var(--sk-content-text-muted)',
                opacity: 0.6,
            }}>
                <IconChevronUp width={9} height={9}/>
                <IconChevronDown width={9} height={9} style={{ marginTop: -2 }}/>
            </span>
        );
    }
    return (
        <span style={{ display: 'inline-flex', marginLeft: 4, color: '#b08620' }}>
            {dir === 'asc' ? <IconChevronUp width={12} height={12}/> : <IconChevronDown width={12} height={12}/>}
        </span>
    );
}
const Students = () => {
    const navigate = useNavigate();
    const [students, setStudents] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredStudents, setFilteredStudents] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [courses, setCourses] = useState([]);

    // Состояние сортировки
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    const [newStudent, setNewStudent] = useState({
        ferstName: '',
        lastName: '',
        age: '',
        nameRoditel: '',
        phoneRod: '',
        tg: '',
        comment: '',
        status: 'Активен'
    });

    // Загрузка студентов
    const [fetchStudents, isLoading, error] = useLoader(async () => {
        try {
            const response = await AdminService.getAllStudents();
            
            setStudents(response.data || []);
            setFilteredStudents(response.data || []);
        } catch (error) {
            logger.error('Ошибка загрузки студентов:', error);
        }
    });

    // Загрузка курсов
    const [fetchCourses, isLoadingCourses] = useLoader(async () => {
        try {
            const response = await AdminService.getAllCourses();
            
            setCourses(response.data || []);
        } catch (error) {
            logger.error('Ошибка загрузки курсов:', error);
        }
    });

    // Создание нового студента
    const [createStudent, isCreating, createError] = useLoader(async () => {
        try {
            const response = await AdminService.createStudent(newStudent);
            if (response.data) {
                setStudents(prev => [...prev, response.data]);
                setFilteredStudents(prev => [...prev, response.data]);
                setShowModal(false);
                setNewStudent({
                    ferstName: '',
                    lastName: '',
                    age: '',
                    nameRoditel: '',
                    phoneRod: '',
                    tg: '',
                    comment: '',
                    status: 'Активен'
                });
            }
        } catch (error) {
            logger.error('Ошибка создания студента:', error);
        }
    });

    // Загрузка данных при монтировании компонента
    useEffect(() => {
        fetchStudents();
        fetchCourses();
    }, []);

    // Фильтрация студентов по поисковому запросу
    useEffect(() => {
        if (!searchTerm) {
            setFilteredStudents(students);
            return;
        }

        const filtered = students.filter(student => {
            const searchLower = searchTerm.toLowerCase();
            return (
                student.ferstName?.toLowerCase().includes(searchLower) ||
                student.lastName?.toLowerCase().includes(searchLower) ||
                student.age?.toString().toLowerCase().includes(searchLower) ||
                student.nameRoditel?.toLowerCase().includes(searchLower) ||
                student.phoneRod?.toLowerCase().includes(searchLower) ||
                student.tg?.toLowerCase().includes(searchLower) ||
                student.comment?.toLowerCase().includes(searchLower) ||
                student.status?.toLowerCase().includes(searchLower)
            );
        });
        setFilteredStudents(filtered);
    }, [searchTerm, students]);

    // Функция сортировки
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });

        const sortedStudents = [...filteredStudents].sort((a, b) => {
            let aValue = a[key];
            let bValue = b[key];

            // Особая обработка для поля имени (объединяем имя и фамилию)
            if (key === 'name') {
                aValue = `${a.ferstName || ''} ${a.lastName || ''}`.trim();
                bValue = `${b.ferstName || ''} ${b.lastName || ''}`.trim();
            }

            // Особая обработка для группы
            if (key === 'group') {
                aValue = a.group && a.group !== "[]" ? JSON.parse(a.group).join(', ') : '';
                bValue = b.group && b.group !== "[]" ? JSON.parse(b.group).join(', ') : '';
            }

            // Обработка возраста как числа
            if (key === 'age') {
                aValue = parseInt(aValue) || 0;
                bValue = parseInt(bValue) || 0;
            }

            if (aValue === null || aValue === undefined) aValue = '';
            if (bValue === null || bValue === undefined) bValue = '';

            if (direction === 'asc') {
                if (key === 'age') {
                    return aValue - bValue;
                } else {
                    return aValue.toString().localeCompare(bValue.toString(), 'ru', { numeric: true });
                }
            } else {
                if (key === 'age') {
                    return bValue - aValue;
                } else {
                    return bValue.toString().localeCompare(aValue.toString(), 'ru', { numeric: true });
                }
            }
        });

        setFilteredStudents(sortedStudents);
    };

    if (isLoading || isLoadingCourses ) {
        return (
            <div className={cl.page_wrapper}>
                <Spinner size="large" text="Загрузка учеников" fullPage />
            </div>
        );
    }

    // Получить иконку сортировки колонки таблицы — SVG-стрелки вместо unicode.
    const getSortIcon = (columnKey) => (
        <SortIcon
            active={sortConfig.key === columnKey}
            dir={sortConfig.direction}
        />
    );

    // Обработка изменений в форме
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewStudent(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Отправка формы
    const handleSubmit = (e) => {
        e.preventDefault();
        if (!newStudent.ferstName || !newStudent.lastName) {
            alert('Пожалуйста, заполните обязательные поля: Имя и Фамилия');
            return;
        }
        createStudent();
    };

    // Получение статуса с цветом
    const getStatusClass = (status) => {
        switch (status?.toLowerCase()) {
            case 'активен':
                return cl.statusActive;
            case 'пробный':
                return cl.statusTrial;
            case 'архив':
                return cl.statusArchive;
            default:
                return cl.statusDefault;
        }
    };

    // Переход к карточке студента
    const handleStudentClick = (studentId) => {
        navigate(`/main/students/${studentId}`);
    };

    // Рендер карточек курсов
    const renderCourseCards = (student) => {
        // logger.log(student.group);

        const studentCourses = getStudentCourses(student.group);

        if (studentCourses.length === 0) {
            return (
                <div className={cl.noCourses}>
                    Не записан
                </div>
            );
        }

        return (
            <div className={cl.courseCardsContainer}>
                {studentCourses.map(course => (
                    <div key={course.id} className={cl.miniCourseCard}>
                        <div className={cl.miniCourseHeader}>
                            <span className={cl.miniCourseName}>{course.name}</span>
                        </div>
                        <div className={cl.miniCourseDetails}>
                            <span className={cl.miniCourseDay}>
                                {daysOfWeek[course.day] || 'Не указан'}
                            </span>
                            <span className={cl.miniCourseTime}>
                                {course.timeStart && course.timeEnd
                                    ? `${course.timeStart}-${course.timeEnd}`
                                    : 'Время не указано'}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // Получить курсы студента по IDs
    const getStudentCourses = (groupString) => {
        // Добавляем проверку на загрузку курсов
        if (!courses || courses.length === 0) return [];

        if (!groupString || groupString === "[]") return [];

        try {
            const courseIds = JSON.parse(groupString);
            return courses.filter(course => courseIds.includes(course.id));
        } catch (error) {
            return [];
        }
    };


    // Дни недели
    const daysOfWeek = {
        0: 'Вс',
        1: 'Пн',
        2: 'Вт',
        3: 'Ср',
        4: 'Чт',
        5: 'Пт',
        6: 'Сб'
    };

    // Считаем активных учеников (для подзаголовка-pill в шапке).
    const activeCount = students.filter(s => s.status === 'Активен').length;

    return (
        <div className={cl.page_wrapper}>
            <div className={cl.wrap_content_1}>
                {/* ==================================================
                    Карточка-шапка страницы:
                    слева иконка + заголовок + микро-метрики (всего/активных),
                    справа жёлтая кнопка «Добавить ученика».
                    Светлый фон с акцентной полосой слева, чтобы не была
                    «плоской» дефолтной шапкой.
                    ================================================== */}
                <div className={cl.pageHeader}>
                    <div className={cl.pageHeaderLeft}>
                        <div className={cl.pageHeaderIcon}>
                            <IconStudents width={22} height={22}/>
                        </div>
                        <div className={cl.pageHeaderText}>
                            <div className={cl.pageHeaderTopRow}>
                                <h1 className={cl.pageHeaderTitle}>Ученики</h1>
                                <span className={cl.pageHeaderPill}>
                                    <span className={cl.pageHeaderPillStrong}>{students.length}</span>
                                    <span className={cl.pageHeaderPillMuted}>всего</span>
                                </span>
                                {activeCount > 0 && (
                                    <span className={`${cl.pageHeaderPill} ${cl.pageHeaderPillActive}`}>
                                        <span className={cl.pageHeaderPillDot}/>
                                        <span className={cl.pageHeaderPillStrong}>{activeCount}</span>
                                        <span className={cl.pageHeaderPillMuted}>активных</span>
                                    </span>
                                )}
                            </div>
                            <div className={cl.breadcrumbs}>
                                АдминПанель · Студенты
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={() => setShowModal(true)}
                        className={cl.addButton}
                        disabled={isCreating}
                    >
                        <IconPlus width={16} height={16}/>
                        <span>{isCreating ? 'Добавление…' : 'Добавить ученика'}</span>
                    </button>
                </div>

                <PageIntro
                    storageKey="students"
                    title="О странице «Ученики»"
                    text="Здесь собраны все ученики вашего центра: имя, возраст, статус и контактные данные родителей. На этой странице вы заводите карточку ученика — потом её можно подключить к курсам и аккаунту Майнкрафтии."
                    bullets={[
                        'Поиск работает по имени, фамилии и любому полю в таблице',
                        'Клик по строке открывает карточку ученика с курсами и платежами',
                        'Кнопка «Добавить ученика» — создать новую карточку',
                        'Сортировка по столбцам — клик на заголовок (имя, возраст, дата)',
                    ]}
                />

                {/* Поисковая строка */}
                <div className={cl.searchContainer}>
                    <span className={cl.searchIcon}>
                        <IconSearch width={18} height={18}/>
                    </span>
                    <input
                        type="text"
                        placeholder="Поиск учеников..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={cl.searchInput}
                    />
                </div>



                {/* Таблица студентов */}
                {error && <div className={cl.error}>Ошибка загрузки студентов</div>}

                {filteredStudents.length === 0 && !isLoading ? (
                    <div className={cl.noData}>Студенты не найдены</div>
                ) : (
                    <div className={cl.tableContainer}>
                        <table className={cl.studentsTable}>
                            <thead>
                                <tr>
                                    <th
                                        onClick={() => handleSort('id')}
                                        style={{ cursor: 'pointer', width: 50, textAlign: 'center' }}
                                    >
                                        №{getSortIcon('id')}
                                    </th>
                                    <th
                                        onClick={() => handleSort('name')}
                                        style={{ cursor: 'pointer', textAlign: 'center', width: 150 }}
                                    >
                                        Имя{getSortIcon('name')}
                                    </th>
                                    <th
                                        onClick={() => handleSort('age')}
                                        style={{ cursor: 'pointer', width: 80, textAlign: 'center' }}
                                    >
                                        Возраст{getSortIcon('age')}
                                    </th>
                                    <th
                                        onClick={() => handleSort('nameRoditel')}
                                        style={{ cursor: 'pointer', width: 120, textAlign: 'center' }}
                                    >
                                        Имя родителя{getSortIcon('nameRoditel')}
                                    </th>
                                    <th
                                        onClick={() => handleSort('phoneRod')}
                                        style={{ cursor: 'pointer', width: 150, textAlign: 'center' }}
                                    >
                                        Телефон родителя{getSortIcon('phoneRod')}
                                    </th>
                                    <th
                                        onClick={() => handleSort('group')}
                                        style={{ cursor: 'pointer', width: 200, textAlign: 'center' }}
                                    >
                                        Группа{getSortIcon('group')}
                                    </th>
                                    <th
                                        onClick={() => handleSort('comment')}
                                        style={{ cursor: 'pointer', textAlign: 'center' }}
                                    >
                                        Комментарии{getSortIcon('comment')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredStudents.map((student, index) => (
                                    <tr
                                        key={student.id}
                                        className={cl.studentRow}
                                        onClick={() => handleStudentClick(student.id)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <td data-label="№" style={{ textAlign: 'center' }}>{index + 1}</td>
                                        <td data-label="Имя">
                                            <div className={cl.studentName}>
                                                {student.ferstName} {student.lastName}
                                            </div>
                                            <div className={getStatusClass(student.status)}>
                                                {student.status}
                                            </div>
                                        </td>
                                        <td data-label="Возраст" style={{ textAlign: 'center' }}>{student.age}</td>
                                        <td data-label="Имя родителя">{student.nameRoditel}</td>
                                        <td data-label="Телефон">{student.phoneRod}</td>
                                        <td data-label="Группа">
                                            {renderCourseCards(student)}
                                        </td>
                                        <td data-label="Комментарий">{student.comment || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Модальное окно добавления студента */}
                {showModal && (
                    <div className={cl.modalOverlay} onClick={() => setShowModal(false)}>
                        <div className={cl.modalContent} onClick={(e) => e.stopPropagation()}>
                            <div className={cl.modalHeader}>
                                <h2>Добавить нового ученика</h2>
                                <button
                                    className={cl.closeButton}
                                    onClick={() => setShowModal(false)}
                                >
                                    ×
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className={cl.studentForm}>
                                <div className={cl.formRow}>
                                    <div className={cl.formGroup}>
                                        <label>Имя *</label>
                                        <input
                                            type="text"
                                            name="ferstName"
                                            value={newStudent.ferstName}
                                            onChange={handleInputChange}
                                            className={cl.formInput}
                                            required
                                        />
                                    </div>
                                    <div className={cl.formGroup}>
                                        <label>Фамилия *</label>
                                        <input
                                            type="text"
                                            name="lastName"
                                            value={newStudent.lastName}
                                            onChange={handleInputChange}
                                            className={cl.formInput}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className={cl.formRow}>
                                    <div className={cl.formGroup}>
                                        <label>Возраст</label>
                                        <input
                                            type="number"
                                            name="age"
                                            value={newStudent.age}
                                            onChange={handleInputChange}
                                            className={cl.formInput}
                                            min="1"
                                            max="100"
                                        />
                                    </div>
                                    <div className={cl.formGroup}>
                                        <label>Статус</label>
                                        <select
                                            name="status"
                                            value={newStudent.status}
                                            onChange={handleInputChange}
                                            className={cl.formInput}
                                        >
                                            <option value="Активен">Активен</option>
                                            <option value="Пробный">Пробный</option>
                                            <option value="Архив">Архив</option>
                                        </select>
                                    </div>
                                </div>

                                <div className={cl.formRow}>
                                    <div className={cl.formGroup}>
                                        <label>Имя родителя</label>
                                        <input
                                            type="text"
                                            name="nameRoditel"
                                            value={newStudent.nameRoditel}
                                            onChange={handleInputChange}
                                            className={cl.formInput}
                                        />
                                    </div>
                                    <div className={cl.formGroup}>
                                        <label>Телефон родителя</label>
                                        <input
                                            type="tel"
                                            name="phoneRod"
                                            value={newStudent.phoneRod}
                                            onChange={handleInputChange}
                                            className={cl.formInput}
                                        />
                                    </div>
                                </div>

                                <div className={cl.formGroup}>
                                    <label>Telegram</label>
                                    <input
                                        type="text"
                                        name="tg"
                                        value={newStudent.tg}
                                        onChange={handleInputChange}
                                        className={cl.formInput}
                                        placeholder="@username"
                                    />
                                </div>

                                <div className={cl.formGroup}>
                                    <label>Комментарий</label>
                                    <textarea
                                        name="comment"
                                        value={newStudent.comment}
                                        onChange={handleInputChange}
                                        className={cl.formTextarea}
                                        rows="3"
                                    />
                                </div>

                                <div className={cl.formActions}>
                                    <button
                                        type="button"
                                        onClick={() => setShowModal(false)}
                                        className={cl.cancelButton}
                                    >
                                        Отмена
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isCreating}
                                        className={cl.submitButton}
                                    >
                                        {isCreating ? 'Создание...' : 'Добавить студента'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Students;